import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { SingleValue } from 'react-select'

import {
	AutocompleteMultiple,
	Button,
	EmptyRow,
	MarkInput,
	Pagination,
	Spinner,
	Table,
	TagsActions,
	Td,
	Tr,
} from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Divider from '@components/Divider'
import PageTitle from '@components/PageTitle'
import Template from '@components/Template'
import { successMessages } from '@constants/feedbackMessages'
import { yupResolver } from '@hookform/resolvers/yup'
import {
	FilterStateProps,
	InitOptionsFilterResponse,
} from '@models/questions.model'
import { addQuizSchema } from '@schemas/addQuiz.schema'
import { categoryApi } from '@services/CategoryApi'
import { examsApi } from '@services/ExamsApi'
import { questionsApi } from '@services/QuestionsApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import {
	createBreadcrumbLink,
	useCreateBreadcrumb,
	useQuery,
} from '@utils/hooks'

type Inputs = {
	discipline: SingleValue<{ value: number; label: string }>
	subject: SingleValue<{ value: number; label: string }>
	subSubject: SingleValue<{ value: number; label: string }>
}

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
}

function AddQuestion() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const [questionIds, setQuestionIds] = useState<number[]>([])

	const { id, idSimulated } = useParams<{
		id: string
		idSimulated: string
	}>()

	const { data: categoryInfo } = categoryApi.useShowQueryQuery({
		id: String(id) as string,
	})

	const { data: examsInfo } = examsApi.useShowQueryQuery({
		id: String(idSimulated) as string,
	})

	const [addQuestions, { isSuccess }] = questionsApi.useAddMutation()

	const allPages = useMemo(() => {
		return [
			createBreadcrumbLink(`Categorias`, `/categorias`, 1),
			createBreadcrumbLink(`${categoryInfo?.name}`, `/categorias/${id}`, 2),
			createBreadcrumbLink(
				`${examsInfo?.name}`,
				`/categorias/${id}/simulado/${idSimulated}`,
				3
			),
			createBreadcrumbLink(
				`Adicionar questão`,
				`/categorias/${id}/simulado/${idSimulated}/adicionar-questao`,
				4
			),
		]
	}, [categoryInfo?.name, examsInfo?.name, id, idSimulated])

	const pages = useCreateBreadcrumb(allPages)

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { control } = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addQuizSchema),
	})

	const onSubmit = () => {
		const newQuestionIds = questionIds.filter(
			(numero, index, self) => self.indexOf(numero) === index
		)

		addQuestions({ questionIds: newQuestionIds, mockExamId: idSimulated })
	}

	useEffect(() => {
		if (isSuccess) {
			navigate(`/categorias/${id}/simulado/${idSimulated}`)
			dispatch(notificationCreators.show(successMessages.storedQuiz))
		}
	}, [isSuccess, dispatch, navigate, id, idSimulated])

	const query = useQuery()
	const pageQuery = Number(query.get('page')) || 1
	const [page, setPage] = useState(pageQuery)
	const [initOptions, setInitOptions] = useState<InitOptionsFilterResponse>({
		articles: [],
		disciplines: [],
		institutions: [],
		materId: [],
		subMaterId: [],
		juries: [],
		roles: [],
		years: [],
	})
	const [values, setValues] = useState<FilterStateProps>({
		disciplines: [],
		materId: [],
		subMaterId: [],
		year: [],
		office: [],
		juries: [],
		institutions: [],
	})

	const { data: dataQuestions, isLoading: isLoadingQuestions } =
		questionsApi.useListQuestionsQuery({
			page,
			disciplineIds: values.disciplines.map((value) => value.id).join(','),
			matterIds: values.materId.map((value) => value.id).join(','),
			submatterIds: values.subMaterId.map((value) => value.id).join(','),
			mockId: idSimulated,
			yearIds: values.year.map((value) => value.id).join(','),
			positionIds: values.office.map((value) => value.id).join(','),
			juryIds: values.juries.map((value) => value.id).join(','),
			institutionIds: values.institutions.map((value) => value.id).join(','),
		})

	const [
		GetAllOptionsFilter,
		{ isSuccess: isSuccessAllFilter, data: dataAllFilters },
	] = questionsApi.useAllOptionsFilterMutation()

	const [getOptionsMatter, { data: dataMatter, isSuccess: isSuccessMatter }] =
		questionsApi.useOptionMatterMutation()

	const [
		getOptionsSubMatter,
		{ data: dataSubMatter, isSuccess: isSuccessSubMatter },
	] = questionsApi.useOptionSubMatterMutation()

	const tagActive =
		values.institutions.length > 0 ||
		values.disciplines.length > 0 ||
		values.materId.length > 0 ||
		values.subMaterId.length > 0 ||
		values.year.length > 0 ||
		values.office.length > 0 ||
		values.juries.length > 0

	const handleFilterClear = () => {
		setValues({
			disciplines: [],
			materId: [],
			subMaterId: [],
			year: [],
			office: [],
			juries: [],
			institutions: [],
		})
	}

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useEffect(() => {
		GetAllOptionsFilter()
	}, [GetAllOptionsFilter])

	useEffect(() => {
		if (isSuccessAllFilter && dataAllFilters) {
			setInitOptions(dataAllFilters)
		}
	}, [isSuccessAllFilter, dataAllFilters])

	useEffect(() => {
		if (values.disciplines.length > 0) {
			getOptionsMatter(values.disciplines.map((value) => value.id).join(','))
		} else {
			setValues((prev) => ({ ...prev, materId: [] }))
			setInitOptions((prev) => ({ ...prev, materId: [] }))
		}
	}, [values.disciplines, getOptionsMatter])

	useEffect(() => {
		if (isSuccessMatter && dataMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				materId: dataMatter.data,
			}))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessMatter, dataMatter])

	useEffect(() => {
		if (values.materId.length > 0) {
			getOptionsSubMatter(values.materId.map((value) => value.id).join(','))
		} else {
			setValues((prev) => ({ ...prev, subMaterId: [] }))
			setInitOptions((prev) => ({ ...prev, subMaterId: [] }))
		}
	}, [values.materId, getOptionsSubMatter])

	useEffect(() => {
		if (isSuccessSubMatter && dataSubMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				subMaterId: dataSubMatter.data,
			}))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessSubMatter, dataSubMatter])

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Adicionar Questão' />
				<div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-4 mt-7'>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								disciplines: item,
							}))
						}}
						options={initOptions.disciplines}
						selectedValue={values.disciplines}
						label='Código/Lei'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								materId: item,
							}))
						}}
						options={initOptions.materId || []}
						selectedValue={values.materId}
						label='Assunto'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								subMaterId: item,
							}))
						}}
						options={initOptions.subMaterId || []}
						selectedValue={values.subMaterId}
						label='SubAssunto'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								year: item,
							}))
						}}
						options={initOptions.years}
						selectedValue={values.year}
						label='Ano'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								office: item,
							}))
						}}
						options={initOptions.roles}
						selectedValue={values.office}
						label='Cargo'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								juries: item,
							}))
						}}
						options={initOptions.juries}
						selectedValue={values.juries}
						label='Banca'
					/>
					<AutocompleteMultiple
						onChange={(item) => {
							setValues((prevState) => ({
								...prevState,
								institutions: item,
							}))
						}}
						options={initOptions.institutions}
						selectedValue={values.institutions}
						label='Instituição'
					/>
				</div>

				<div className='flex gap-5 justify-end mb-8'>
					<Button variant='danger' onClick={handleFilterClear}>
						Limpar Filtros
					</Button>
					<Button
						className='h-10'
						onClick={onSubmit}
						disabled={questionIds.length === 0}
					>
						Adicionar Questões
					</Button>
				</div>

				{tagActive && (
					<div className='flex flex-wrap gap-2 justify-between mt-5 mb-5'>
						<TagsActions
							title='Código/Lei'
							name='disciplines'
							options={values.disciplines}
							onClose={setValues}
						/>
						<TagsActions
							title='Assunto'
							name='materId'
							options={values.materId}
							onClose={setValues}
						/>
						<TagsActions
							title='SubAssunto'
							name='subMaterId'
							options={values.subMaterId}
							onClose={setValues}
						/>
						<TagsActions
							title='Ano'
							name='year'
							options={values.year}
							onClose={setValues}
						/>
						<TagsActions
							title='Cargo'
							name='office'
							options={values.office}
							onClose={setValues}
						/>
						<TagsActions
							title='Banca'
							name='juries'
							options={values.juries}
							onClose={setValues}
						/>
						<TagsActions
							title='Instituição'
							name='institutions'
							options={values.institutions}
							onClose={setValues}
						/>
					</div>
				)}

				{isLoadingQuestions ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{
								label: 'id',
							},
							{
								label: 'nome',
							},
							{
								label: 'selecionar',
							},
						]}
					>
						{dataQuestions?.data && dataQuestions?.data.length > 0 ? (
							dataQuestions?.data.map(({ id: questionId, title }) => (
								<Tr key={questionId}>
									<Td>{questionId}</Td>
									<Td className='w-[100%] px-6'>{title}</Td>

									<Td>
										<MarkInput
											type='checkbox'
											itens={[
												{ id: questionId as unknown as string, title: '' },
											]}
											multiCheckbox
											onChange={(e) => {
												if (e.currentTarget.checked) {
													setQuestionIds([...questionIds, questionId])
												} else {
													setQuestionIds(
														questionIds.filter(
															(number) => number !== questionId
														)
													)
												}
											}}
										/>
									</Td>
								</Tr>
							))
						) : (
							<EmptyRow tdAmount={3} message='Nenhuma questão encontrada' />
						)}
					</Table>
				)}
				<Divider className={styles.divider} />
				{dataQuestions?.data && dataQuestions?.data.length > 0 && (
					<Pagination
						pageCount={dataQuestions?.meta.lastPage || 0}
						maxAmount={dataQuestions?.meta.total || 0}
						perPage={dataQuestions?.meta.perPage || 0}
					/>
				)}
			</Template>
		</>
	)
}

export default AddQuestion
